import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-property-details',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="surface-card p-6 md:p-8 border border-border shadow-sm antialiased select-none"
    >
      <!-- ── PROPERTY HEADER TITLE & LOCATION ── -->
      <div class="mb-6">
        <h1 class="text-xl md:text-2xl font-black text-text-primary tracking-tight leading-snug mb-3">
          {{ displayText(propertyData?.title, 'تفاصيل الوحدة') }}
        </h1>
        <div
          class="inline-flex items-center gap-2 px-3 py-1.5 bg-bg-secondary rounded-xl border border-border-subtle text-xs text-text-secondary font-medium"
        >
          <i class="fa-solid fa-location-dot text-primary"></i>
          <span>{{ displayText(propertyData?.location?.full, 'الموقع غير محدد') }}</span>
        </div>
      </div>

      <!-- ── CORE SPECS HERO GRID (4 COLUMNS) ── -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 pb-6 border-b border-border-subtle">
        <!-- Bedrooms Card -->
        <div
          class="flex items-center gap-3 p-3.5 bg-bg-secondary/60 border border-border-subtle rounded-2xl transition-all hover:bg-bg-secondary"
        >
          <div
            class="w-9 h-9 rounded-xl bg-surface-elevated border border-border flex items-center justify-center text-text-secondary shrink-0 shadow-sm"
          >
            <i class="fa-solid fa-bed text-xs text-text-muted"></i>
          </div>
          <div>
            <span
              class="block text-[10px] font-bold text-text-muted uppercase tracking-wider font-mono"
              >غرف النوم</span
            >
            <span class="text-sm font-black font-mono text-text-primary">{{
              formatCount(propertyData?.specs?.bedrooms)
            }}</span>
          </div>
        </div>

        <!-- Bathrooms Card -->
        <div
          class="flex items-center gap-3 p-3.5 bg-bg-secondary/60 border border-border-subtle rounded-2xl transition-all hover:bg-bg-secondary"
        >
          <div
            class="w-9 h-9 rounded-xl bg-surface-elevated border border-border flex items-center justify-center text-text-secondary shrink-0 shadow-sm"
          >
            <i class="fa-solid fa-bath text-xs text-text-muted"></i>
          </div>
          <div>
            <span
              class="block text-[10px] font-bold text-text-muted uppercase tracking-wider font-mono"
              >الحمامات</span
            >
            <span class="text-sm font-black font-mono text-text-primary">{{
              formatCount(propertyData?.specs?.bathrooms)
            }}</span>
          </div>
        </div>

        <!-- Area Card -->
        <div
          class="flex items-center gap-3 p-3.5 bg-bg-secondary/60 border border-border-subtle rounded-2xl transition-all hover:bg-bg-secondary"
        >
          <div
            class="w-9 h-9 rounded-xl bg-surface-elevated border border-border flex items-center justify-center text-text-secondary shrink-0 shadow-sm"
          >
            <i class="fa-solid fa-chart-area text-xs text-text-muted"></i>
          </div>
          <div>
            <span
              class="block text-[10px] font-bold text-text-muted uppercase tracking-wider font-mono"
              >المساحة</span
            >
            <span class="text-sm font-black font-mono text-text-primary">{{
              formatArea(propertyData?.specs?.area_sqm)
            }}</span>
          </div>
        </div>

        <!-- Floor Card -->
        <div
          class="flex items-center gap-3 p-3.5 bg-bg-secondary/60 border border-border-subtle rounded-2xl transition-all hover:bg-bg-secondary"
        >
          <div
            class="w-9 h-9 rounded-xl bg-surface-elevated border border-border flex items-center justify-center text-text-secondary shrink-0 shadow-sm"
          >
            <i class="fa-solid fa-layer-group text-xs text-text-muted"></i>
          </div>
          <div>
            <span
              class="block text-[10px] font-bold text-text-muted uppercase tracking-wider font-mono"
              >الطابق</span
            >
            <span class="text-sm font-black font-mono text-text-primary">{{
              displayText(propertyData?.specs?.floor)
            }}</span>
          </div>
        </div>
      </div>

      <!-- ── SECONDARY SPECIFICATIONS MATRIX GRID ── -->
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5 pt-6 text-sm">
        <div>
          <span
            class="block text-[10px] font-bold uppercase tracking-wider text-text-muted font-mono mb-1"
            >نوع العقار</span
          >
          <span class="font-black text-text-primary">{{
            displayText(propertyData?.specs?.property_type)
          }}</span>
        </div>

        <div>
          <span
            class="block text-[10px] font-bold uppercase tracking-wider text-text-muted font-mono mb-1"
            >حالة الملكية</span
          >
          <span class="font-black text-text-primary">{{
            displayText(propertyData?.specs?.ownership)
          }}</span>
        </div>

        <div>
          <span
            class="block text-[10px] font-bold uppercase tracking-wider text-text-muted font-mono mb-1"
            >التشطيب</span
          >
          <span class="font-black text-text-primary">{{
            displayText(propertyData?.specs?.finishing)
          }}</span>
        </div>

        @if (propertyData?.specs?.year_built > 0) {
          <div>
            <span
              class="block text-[10px] font-bold uppercase tracking-wider text-text-muted font-mono mb-1"
              >سنة البناء</span
            >
            <span class="font-black font-mono text-text-primary">{{
              propertyData?.specs?.year_built
            }}</span>
          </div>
        }

        @if (hasValue(propertyData?.specs?.compound)) {
          <div>
            <span
              class="block text-[10px] font-bold uppercase tracking-wider text-text-muted font-mono mb-1"
              >كمبوند</span
            >
            <span class="font-black text-text-primary">{{ propertyData?.specs?.compound }}</span>
          </div>
        }

        @if (hasValue(propertyData?.specs?.delivery)) {
          <div>
            <span
              class="block text-[10px] font-bold uppercase tracking-wider text-text-muted font-mono mb-1"
              >حالة التسليم</span
            >
            <span
              class="inline-flex items-center gap-1 font-black text-primary bg-primary/10 px-2 py-0.5 rounded border border-primary/20 text-xs font-mono"
            >
              <i class="fa-solid fa-bolt-lightning text-[10px]"></i>
              <span>{{ propertyData?.specs?.delivery }}</span>
            </span>
          </div>
        }

        @if (propertyData?.specs?.adults > 0) {
          <div class="col-span-1 sm:col-span-2">
            <span
              class="block text-[10px] font-bold uppercase tracking-wider text-text-muted font-mono mb-1"
              >القدرة القصوى</span
            >
            <span class="font-black text-text-primary flex items-center gap-1.5">
              <i class="fa-solid fa-users text-text-muted text-xs"></i>
              <span>{{ propertyData?.specs?.adults }} بالغين</span>
              @if (propertyData?.specs?.children > 0) {
                <span class="text-text-muted font-normal font-mono">/</span>
                <span class="text-text-secondary font-medium"
                  >{{ propertyData?.specs?.children }} أطفال</span
                >
              }
            </span>
          </div>
        }

        @if (propertyData?.specs?.status) {
          <div>
            <span
              class="block text-[10px] font-bold uppercase tracking-wider text-text-muted font-mono mb-1"
              >حالة الإعلان</span
            >
            <span
              class="inline-flex items-center gap-1.5 font-black text-xs capitalize text-success bg-success/10 border border-success/20 px-2.5 py-0.5 rounded-full"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-success animate-pulse"></span>
              <span>{{ propertyData?.specs?.status }}</span>
            </span>
          </div>
        }
      </div>
    </div>
  `,
})
export class PropertyDetailsComponent {
  @Input() propertyData: any;

  displayText(value: unknown, fallback: string = 'غير متوفر'): string {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text ? text : fallback;
  }

  hasValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return !!text && text !== 'N/A' && text !== 'غير متوفر';
  }

  formatArea(value: unknown): string {
    if (typeof value === 'number' && value > 0) return `${value} م²`;
    return 'غير متوفر';
  }

  formatCount(value: unknown): string {
    if (typeof value === 'number' && value > 0) return String(value);
    return 'غير متوفر';
  }
}
